import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from '../../../permissions/SessionContext';
import { computeSourceState } from './sourceState';

/**
 * ModernWash 어댑터 엔드포인트 조회 공통 훅.
 *
 * 5개 세차 화면이 같은 패턴(스코프 파라미터, 로딩/오류, 봉투 상태 계산)을 반복하지
 * 않도록 한곳에 모았다. Dashboard/Members 와 동일하게 master 는 storeId 를 보내고
 * manager 는 생략한다(backend 가 강제).
 *
 * @param fetcher api/modernwash 의 함수 (stable 모듈 참조)
 * @param params  화면별 추가 쿼리(예: 차량 화면의 memberId, 페이지네이션).
 *                호출부에서 반드시 useMemo 로 안정 참조를 넘길 것 — 매 렌더 새 객체를
 *                넘기면 무한 재조회가 된다. 생략 시 스코프 파라미터만 보낸다.
 *
 * 반환:
 *   loading   최초/재조회 로딩 여부
 *   reload    수동 재조회 (새로고침·다시 시도 버튼)
 *   envelope  마지막 성공 응답 봉투 (없으면 null)
 *   source    computeSourceState 서술자 (blocking/degraded/문구)
 *   items     envelope.items (없으면 [])
 *   data      envelope.data  (없으면 null)
 *   scope     envelope.scope (없으면 null)
 */
const EMPTY_PARAMS = {};

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

export function useModernwashResource(fetcher, params = EMPTY_PARAMS) {
  const { session } = useSession();
  const [envelope, setEnvelope] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const scopedParams = useMemo(() => getScopedParams(session), [session]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher({ ...scopedParams, ...params });
      setEnvelope(res);
    } catch (err) {
      setError(err);
      setEnvelope(null);
    } finally {
      setLoading(false);
    }
  }, [fetcher, scopedParams, params]);

  useEffect(() => {
    reload();
  }, [reload]);

  const source = useMemo(
    () => computeSourceState({ response: envelope, error }),
    [envelope, error],
  );

  return {
    loading,
    reload,
    envelope,
    source,
    items: Array.isArray(envelope?.items) ? envelope.items : [],
    data: envelope?.data ?? null,
    scope: envelope?.scope ?? null,
  };
}
